import BaseCanvasService from '@src/slide/application/service/canvas-component/core/base/base-canvas-service';
import { IPcCanvasProps } from '@src/slide/application/service/canvas-component/core/pc/type';
import {
    IPcCanvasServiceDelegates,
    IPcCanvasServiceLogic,
    IPcNoteAreaRef,
} from '@src/slide/application/service/canvas-component/core/pc/pc-canvas-service.interface';
import { IPcCanvasInjectable } from '@src/slide/application/service/canvas-component/inject/pc/interface';
import {
    ICanvasSizeHandler,
} from '@src/slide/application/service/canvas-component/core/base/canvas-size-handler/interface';
import debounce from 'lodash/debounce';
import set from 'lodash/set';
import get from 'lodash/get';
import { SyntheticEvent } from 'react';
import {
    ISlideModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { ISlideDataModel } from '@tencent/slide-sdk-model';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { waitWorkbenchMountHandler } from '@utils/workbench';
import WedriveFileManager from '@src/features/wedrive-file-manager';
import { IReactionDisposer, reaction } from 'mobx';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { IMasterModel } from '@src/slide/domain/ui-model/element-modeles/Interface/master-model.interface';
import { RefName } from '@editor/src/page/index/components/canvas/canvas-constants';
import {
    IMasterLayoutEditService,
} from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import PcCanvasModel from '@src/slide/application/service/canvas-component/core/pc/pc-canvas-model';
import { NOTE_COLLAPSE_HEIGHT, NOTE_DEFAULT_HEIGHT } from '@editor/src/js/constants';
import { GraphType } from '@src/slide/infrastructure/module/dependent/shape-info/constant';
import { AutoFitType } from '@src/slide/domain/ui-model/element-modeles/property/text/constants/text-style-type';
import { mobxSet } from '@src/slide/infrastructure/module/independent/assert/get-set';
import { INIT_FOCUS } from '@src/js/constant';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import PcCanvasSizeHandler
    from '@src/slide/application/service/canvas-component/core/pc/canvas-size-handler/pc-canvas-size-handler';
import BaseViewModel from '@src/slide/infrastructure/module/independent/view-service-define/base/base-view-model';
import { IBaseCanvasProps } from '@src/slide/application/service/canvas-component/core/base/type';
import { IBaseNoteAreaInfo } from '../../../note-area-component/core/base/base-note-area-service.interface';

const DEBOUNCE_PERIOD = 600; // slide 名字变更防抖间隔

export default class PcCanvasService extends BaseCanvasService<IPcCanvasProps>
    implements IPcCanvasServiceLogic {
    declare protected services?: IPcCanvasServiceDelegates;
    declare protected inject?: IPcCanvasInjectable;

    private slideNameReaction: IReactionDisposer;
    private editTypeReaction: IReactionDisposer;
    private quickSettingTimer: any;

    private debounceSlideNameChange = debounce((event: SyntheticEvent, name: string, curSlide?: ISlideModel): void => {
        if (!curSlide) {
            return;
        }
        const sdkData = curSlide.sdkData as ISlideDataModel;
        const newName = name;
        if (!newName.length || newName.length > 255) {
            set(event.target, 'value', '');
            return;
        }
        (sdkData as unknown as ISlideDataModel)?.api?.updateName?.(newName);
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.MASTER,
            action: TDWActionType.MASTER_RENAME,
        });
    }, DEBOUNCE_PERIOD);

    public constructor(inject?: IPcCanvasInjectable) {
        super(inject);
    }

    public componentDidMount() {
        super.componentDidMount();

        waitWorkbenchMountHandler((isInWorkbench) => {
            // workbench隐藏placeholder逻辑集成在pc-loader中，非workbench需自行处理
            if (!isInWorkbench) {
                const placeholderContainer = document.getElementById('placeholder-container');
                placeholderContainer?.remove();
            }
        });

        WedriveFileManager.getInstance()?.init?.(
            {},
            this.services?.uiDataService,
            this.services?.selectionService,
            this.services?.editAreaCanvasSizeManager,
        );
        this.addObserve();
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.removeObserve();
    }

    public handlerSlideNameChange = (event: SyntheticEvent): void => {
        event.persist();
        const curSlide = this.getCurrentSlide();
        const newName = get(event.target, 'value');
        this.debounceSlideNameChange(event, newName, curSlide);
    };

    public shouldShowTemplateRecommendation() {
        return this.inject?.shouldShowTemplateRecommendation() ?? true;
    }

    public handleScrollEvent = () => {
        this.getPcScrollService()?.updateCurrentScroll(-this.containerDom.scrollLeft, -this.containerDom.scrollTop);
        this.hideFloatBar();
        this.services?.multiSelectedDashOuterManager?.promise().then((multiSelectedDashOuter) => {
            multiSelectedDashOuter.changeFloatState();
        });
        import(/* webpackChunkName: "service" */ '@com/quick-setting/quick-setting-manager')
            .then((module) => {
                const QuickSettingManager = module.default;
                QuickSettingManager.getInstance().hide();
                this.quickSettingTimer && clearTimeout(this.quickSettingTimer);
                this.quickSettingTimer = setTimeout(() => {
                    QuickSettingManager.getInstance().show();
                    QuickSettingManager.getInstance().updateAnchorRect();
                    window.dispatchEvent(new Event('Scroll-End-Show-LinkFloatBar')); // 滚动停止需要重新让工具栏出现
                }, 500);
            })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
        WedriveFileManager.getInstance()?.hide?.();
    };

    public handleDoubleClick = (e) => { // 双击插入文本框
        if (!this.services?.userPriviledgeService?.isEditable()) {
            this.services?.tipsManager?.showCannotEditTips();
            return;
        }
        const { containerDom } = this;
        const rect = containerDom.getBoundingClientRect();
        const current = {
            left: e.pageX + containerDom.scrollLeft - rect.left - window.pageXOffset,
            top: e.pageY + containerDom.scrollTop - rect.top - window.pageYOffset,
        };
        const {
            slideLeft = 0,
            slideTop = 0,
            scale = 1,
        } = this.services?.editAreaCanvasSizeManager?.getCanvasSize() || { };
        const model = {
            style: {
                position: 'absolute',
                left: (current.left / scale) - (slideLeft / scale),
                top: (current.top / scale) - (slideTop / scale),
                bgShape: GraphType.RECT,
                border: { width: 1, style: 'solid' },
            },
            subType: 1,
        };
        const extraInfo = {
            textBodyStyle: {
                autoFitType: AutoFitType.SHAPE_AUTO_FIT,
                bodyStyle: {
                    whiteSpace: 'pre',
                },
            },
        };
        // 裁剪状态比较特殊，双击创建element的时候，要先把裁剪态退出。
        this.services?.editingStateManager?.updateElementCropState(false);

        const { currentSlideIndex = 0 } = this.services?.selectionService || { };
        const slideModel = this.services?.selectionService?.slides[currentSlideIndex];
        if (slideModel) {
            slideModel.addElement([UIModelType.SHAPE], [model], [extraInfo]);

            const currentSlideChild = this.services?.selectionService?.currentSlide?.children;
            const shape = currentSlideChild?.length && currentSlideChild[currentSlideChild.length - 1];
            shape && mobxSet(shape, INIT_FOCUS, true);

            tdw({
                opername: TDWOperationType.DOC_PPT,
                module: TDWModuleType.PC_EDIT_INSERT,
                action: TDWActionType.CLK_TWO_TEXT,
            });
        }
    };

    public getCurrentSlide(): ISlideModel | undefined {
        return this.services?.selectionService?.currentSlide;
    }

    public getChartManager = () => this.services?.chartManager;

    public handlerSlideNameFocus = (): void => {
        this.services?.selectionService?.selectedElements.clear();
    };

    public onKeydownEnter = (event: KeyboardEvent): void => {
        // enter
        if (event.keyCode === 13) {
            this.handlerSlideNameChange(event as unknown as SyntheticEvent);
        }
    };

    public exitMasterLayoutEdiType = (): void => {
        this.services?.masterLayoutEditService?.changeEditType(EDIT_TYPE.SLIDE);
    };

    protected createCanvasSizeHandler(): ICanvasSizeHandler {
        return new PcCanvasSizeHandler();
    }

    protected getDefaultNoteAreaInfo(): IBaseNoteAreaInfo {
        // TODO: 考虑和 note-area-service 联动?
        const isNoteFold = this.inject?.isNoteFold();
        const isStatusBarFold = this.inject?.isStatusBarFold();
        if (isNoteFold && isStatusBarFold) {
            return { noteAreaHeight: 0 };
        }
        return { noteAreaHeight: isNoteFold ? NOTE_COLLAPSE_HEIGHT : NOTE_DEFAULT_HEIGHT };
    }

    protected createViewModel(): BaseViewModel<IBaseCanvasProps> {
        return new PcCanvasModel();
    }

    protected getInitModelValue(): Partial<PcCanvasModel> | undefined {
        return {
            ...super.getInitModelValue(),
            canvasSize: this.canvasSizeHandler.getCanvasSize(
                this.inject?.isHeaderFold(),
                this.getDefaultNoteAreaInfo(),
                this.inject?.isStatusBarFold(),
            ),
            curCanvasWrapperStyle: this.getCurCanvasWrapperStyle(),
        };
    }

    protected deselect() {
        this.services?.selectedAnims?.clear();
        super.deselect();
    }

    protected getHeaderFold() {
        const result = this.inject?.isHeaderFold();
        if (typeof result !== 'undefined') {
            return result;
        }
        return this.isHeaderFold;
    }

    protected get noteAreaRef(): IPcNoteAreaRef | undefined {
        return super.noteAreaRef as IPcNoteAreaRef;
    }

    protected fixNoteHeightByResize(): void {
        this.noteAreaRef?.fixNoteHeightByResize();
    }

    private getCurCanvasWrapperStyle() {
        return this.services?.masterLayoutStyleService?.getCurCanvasWrapperStyle();
    }

    private addObserve() {
        this.editTypeReaction = reaction(
            () => this.getMasterLayoutEditService()?.isMasterEditType,
            (isMasterEditType) => {
                this.updateModel<PcCanvasModel>(
                    {
                        isMasterEditType,
                        curCanvasWrapperStyle: this.getCurCanvasWrapperStyle(),
                    },
                    () => {
                        this.resize(null, null, { noteHeightChange: false });
                        if (isMasterEditType) {
                            this.slideNameReaction = reaction(
                                () => {
                                    // eslint-disable-next-line max-len
                                    const { currentSlide } = this.services?.selectionService || { };
                                    // 名字可能正在更新中，故名字相同的不同版式也触发更新
                                    let slideName = currentSlide?.name;
                                    if (currentSlide?.type === UIModelType.MASTER) {
                                        // 3.0中 母版标题在对应的主题中获取
                                        slideName = (currentSlide as IMasterModel).themeName;
                                    }
                                    return {
                                        slideName,
                                        listIndex: currentSlide?.listIndex,
                                    };
                                },
                                ({ slideName }) => {
                                    const input = this.viewDelegate?.getElementById(RefName.NAME_INPUT);
                                    input && (input.value = this.inject?.updateSlideName(slideName));
                                },
                                { fireImmediately: true },
                            );
                        } else {
                            this.slideNameReaction?.();
                        }
                    },
                );
            },
        );
    }

    private removeObserve() {
        this.slideNameReaction?.();
        this.editTypeReaction?.();
    }

    private getMasterLayoutEditService(): IMasterLayoutEditService | undefined {
        if (!this.services?.userPriviledgeService?.isEditable()) {
            return undefined;
        }
        return this.services?.masterLayoutEditService;
    }

    private getPcScrollService() {
        return this.services?.pcScrollService;
    }
}
